import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from '@/lib/use-auth';
import type { Role } from '@/lib/auth-context';

interface RequireAuthProps {
  children: ReactNode;
  /** Si se especifica, exige que el usuario tenga uno de los roles. */
  roles?: Role[];
}

/**
 * Bloquea el render hasta que `useAuth().loading` se resuelve, redirige a
 * `/login` si el usuario no está autenticado, y a `/` si tiene sesión pero
 * no cumple los roles requeridos.
 */
export function RequireAuth({ children, roles }: RequireAuthProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-sm text-muted-foreground">
        Cargando…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si el usuario está obligado a cambiar contraseña, no le dejamos ir a
  // ningún sitio salvo a /password/change.
  if (user.passwordChangeRequired && location.pathname !== '/password/change') {
    return <Navigate to="/password/change" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
